"use client";

import { Section } from "@/components/ui/section";

interface PlaceMapProps {
  businessName: string;
  address: string | null;
  googlePlaceId: string | null;
  phone?: string | null;
  website?: string | null;
  city?: string | null;
  country?: string | null;
  /** Reduce iframe height for use in narrower/inline contexts */
  compact?: boolean;
}

function getEmbedUrl(businessName: string, address: string | null, googlePlaceId: string | null, city?: string | null): string {
  const apiKey = process.env.NEXT_PUBLIC_GOOGLE_MAPS_EMBED_KEY || process.env.NEXT_PUBLIC_GOOGLE_PLACES_API_KEY || "";

  if (googlePlaceId && apiKey) {
    return `https://www.google.com/maps/embed/v1/place?key=${apiKey}&q=place_id:${googlePlaceId}`;
  }

  // Fallback: use search query embed (works without API key via iframe)
  const query = address
    ? `${businessName}, ${address}`
    : city
      ? `${businessName}, ${city}`
      : businessName;
  return `https://maps.google.com/maps?q=${encodeURIComponent(query)}&output=embed`;
}

export function PlaceMap({ businessName, address, googlePlaceId, phone, website, city, country, compact }: PlaceMapProps) {
  // Show the map as long as we have a business name — search-based embed handles the rest
  if (!businessName) return null;

  const locationLabel = [city, country].filter(Boolean).join(", ");

  return (
    <Section title="Location">
      <div className="space-y-[var(--space-3)]">
        {/* Map embed — responsive aspect ratio on mobile, fixed height on desktop */}
        <div className="overflow-hidden rounded-[var(--radius-xl)] border border-[var(--color-border-subtle)]">
          <div className={compact ? "h-[160px]" : "h-[200px] sm:h-[250px]"}>
            <iframe
              title={`Map of ${businessName}`}
              src={getEmbedUrl(businessName, address, googlePlaceId, city)}
              width="100%"
              height="100%"
              style={{ border: 0, display: "block" }}
              allowFullScreen={false}
              loading="lazy"
              referrerPolicy="no-referrer-when-downgrade"
            />
          </div>
        </div>

        {/* Address & contact info */}
        <div className="flex flex-col gap-[var(--space-2)]">
          <div className="space-y-[var(--space-1)] min-w-0">
            {address && (
              <p className="text-[length:var(--font-size-sm)] text-[var(--color-text-primary)] break-words">
                {address}
              </p>
            )}
            {!address && locationLabel && (
              <p className="text-[length:var(--font-size-sm)] text-[var(--color-text-muted)]">
                {locationLabel}
              </p>
            )}
            <div className="flex flex-wrap gap-x-[var(--space-4)] gap-y-[var(--space-1)]">
              {phone && (
                <a
                  href={`tel:${phone}`}
                  className="text-[length:var(--font-size-xs)] text-[var(--color-text-muted)] hover:text-[var(--color-primary)] transition-colors"
                >
                  {phone}
                </a>
              )}
              {website && (
                <a
                  href={website}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-[length:var(--font-size-xs)] text-[var(--color-text-muted)] hover:text-[var(--color-primary)] transition-colors truncate max-w-full sm:max-w-[260px]"
                >
                  {(() => { try { return new URL(website).hostname; } catch { return website; } })()}
                </a>
              )}
            </div>
          </div>
        </div>
      </div>
    </Section>
  );
}
